import { toast } from "@heroui/react"
import { Button, InputGroup, Label, TextField } from "~/lib/heroui"
import { useEffect, useRef, useState } from "react"
import { isPhoneNumber, requestPhoneCode } from "../lib/auth"

type PhoneCodeFieldProps = {
  purpose?: "signup" | "password_reset"
}

export function PhoneCodeField({ purpose = "signup" }: PhoneCodeFieldProps) {
  const [countdown, setCountdown] = useState(0)
  const [pending, setPending] = useState(false)
  const phoneInput = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (!countdown) return
    const timer = window.setInterval(
      () => setCountdown((value) => Math.max(value - 1, 0)),
      1000,
    )
    return () => window.clearInterval(timer)
  }, [countdown])

  async function sendCode() {
    const phone = phoneInput.current?.value.trim() ?? ""
    if (!isPhoneNumber(phone)) {
      toast.danger("请输入有效的 11 位手机号码")
      return
    }

    setPending(true)
    try {
      const result = await requestPhoneCode(phone, purpose)
      setCountdown(60)
      toast.info(
        result.debug_code
          ? `开发环境验证码：${result.debug_code}`
          : result.message,
      )
    } catch (error) {
      toast.danger(
        error instanceof Error ? error.message : "验证码发送失败，请稍后再试",
      )
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <TextField isRequired name="phone_number" type="tel">
        <Label>手机号码</Label>
        <InputGroup variant="secondary">
          <InputGroup.Input
            autoComplete="tel"
            autoFocus
            inputMode="numeric"
            maxLength={11}
            placeholder="请输入 11 位手机号码"
            ref={phoneInput}
          />
        </InputGroup>
      </TextField>
      <TextField isRequired name="verification_code">
        <Label>短信验证码</Label>
        <InputGroup variant="secondary">
          <InputGroup.Input
            autoComplete="one-time-code"
            inputMode="numeric"
            maxLength={6}
            placeholder="输入验证码"
          />
          <InputGroup.Suffix className="verification-code-suffix">
            <Button
              className="verification-code-button"
              isDisabled={countdown > 0}
              isPending={pending}
              onPress={sendCode}
              size="sm"
              type="button"
              variant="ghost"
            >
              {countdown ? `${countdown}s 后重发` : "获取验证码"}
            </Button>
          </InputGroup.Suffix>
        </InputGroup>
      </TextField>
    </>
  )
}
